/**
 * @fileOverview Base carousel.
 * @author Selectra Front End Team
 * @version 1.0.0
 * @requires Polyfill:array_from 'array-from'
 * @requires Polyfill:element_prototype_classlist 'classlist'
 * @requires Polyfill:nodelist_prototype_foreach 'nodelist-foreach'
 */

import debounce from 'lodash-es/throttle';

/**
 * ### Starting Cycle
 *
 * 1 Initialization options
 * 2 Initialization nodes
 * 3 Initialization states
 * 4 Build carousel
 * 5 Attach carousel to DOM
 * 6 Run automatized action
 * 7 Detach Carousel to DOM
 *
 */

/**
 * Private Variables
 */

export default class BaseCarousel {
  constructor( options = {}) {
    // LOGIC
    /* 1 Initialization options */
    options = this.initializeOptions( options );


    // If the selector is not working break the carousel
    if ( !document.querySelector( this.getOption( 'selector' ))) {
      return null;
    }

    /* 2 Initialization carousel nodes */
    this.initializeNodes();

    /* 3 Initialization carousel states */
    this.initializeStates( options );

    /* 4 Build Carousel */
    /* 5 Attach Carousel to the DOM */
    /* 6 Run */
    this.load();

    window.addEventListener( 'resize', debounce( this.onWindowResize.bind( this ), 500 ))
  }


  initializeOptions( options ) {
    this.options = {
      items: options.items || 1,
      selector: options.selector,
      alignment: options.alignment || 'top',
      slideBy: options.slideBy || 1,
      auto: options.auto || false,
      autoTimeout: options.autoTimeout || 2000,
      autoDirection: options.autoDirection || 'forward',
      controls: options.controls === false ? false : true,
      controlsAbsolute: options.controlsAbsolute || false,
      nav: options.nav === false ? false : true,
      navAbsolute: options.navAbsolute || false,
      clones: options.clones === false ? false : true,
      disable: options.disable || false,
      swipe: options.swipe === false ? false : true,
      swipeDistance: options.swipeDistance || 100,
      responsive: options.responsive || {},
    };
    return options;
  }

  initializeNodes() {
    this.listNode = document.querySelector( this.getOption( 'selector' ));
    this.slides = Array.from( this.listNode.querySelectorAll( `${this.getOption( 'selector' )} > *` ));
    this.carouselNode;
    this.controlsNode;
    this.wrapperNode;
    this.navNode;
    this.previousCloneSlides = [];
    this.nextCloneSlides = [];
  }

  // eslint-disable-next-line no-unused-vars
  initializeStates( options ) {
    this.states = {
      // Reference Active Slide (this slide is use as reference to calculate the next and the previous slides)
      referenceSlide: 0,
      // Carousel list Position
      listNodeTransformPosition: 0,
      // Length
      slidesNumber: this.slides.length,
      // PrevSlides
      timerId: null,
      disable: true,
      breakpoint: 0,
      swipeX: 0,
    }
  }

  build() {
    this._buildList();
    this._buildOriginalSlides();
    this._buildCloneSlides()
    this._buildCarouselContainer();
    this._buildControls();
    this._buildNav();
    this._buildWrapper();

    // Method required before attaching carousel parts to the DOM
    this._setSlidesSize();
    this._setOriginalreferenceSlide();
  }

  attach() {
    this._attachCarousel();
    this._attachControls();
    this._attachList();
    this._attachWrapper();
    this._attachNav();
    this._attachClones();

    // Set the position default position
  }

  run() {
    this.play();
    this.getBreakpoint();
    this._setCurrentPosition();
    this._renderPosition();
    this._setNavActiveSlide();
    this._setControlsPosition();

    this._loadSwipe();
  }

  detach() {
    // before cancel all running scripts
    this.stop();
    this._detachClones();
    this._removeAllReferenceClasses();

    if ( this.getOption( 'disable' ) && this.states.disable == false || this.states.disable == false ) {
      this._unsetSlidesSize();
      this.states.disable = true;

      // detach DOM Element
      this._detachControls();
      this._detachNav();
      this._detachListNode();
      this._detachWrapper();
      this._detachCarousel();
    }
  }


  destroy() {

    if ( this.states.disable ) {
      return;
    }
    this.stop();
    this._detachClones();
    this._removeAllReferenceClasses();
    // if(this.getOption('disable') && this.states.disable == false) {
    this._unsetSlidesSize();
    this.states.disable = true;

    // detach DOM Element
    this._detachControls();
    this._detachNav();
    this._detachListNode();
    this._detachWrapper();
    this._detachCarousel();
  }

  play() {
    if ( this.getOption( 'auto' )) {
      if ( this.getOption( 'autoDirection' ) === 'forward' ) {
        this.states.timerId = setTimeout(() => {
          this.goNext(); this.play()
        }, this.getOption( 'autoTimeout' ));
      } else if ( this.getOption( 'autoDirection' ) === 'backward' ) {
        this.states.timerId = setTimeout(() => {
          this.goPrevious(); this.play()
        }, this.getOption( 'autoTimeout' ));
      }
    }
  }

  stop() {
    if ( this.getOption( 'auto' )) {
      clearTimeout( this.states.timerId );
      this.states.timerId = null;
    }
  }

  getAllSlides() {
    return [ ...this.previousCloneSlides, ...this.slides, ...this.nextCloneSlides ]
  }

  getOption( name ) {
    let optValue = this.options[ name ];
    for ( let bp in this.options.responsive ) {
      if (( window.matchMedia( `(min-width: ${bp}px)` ).matches )) {
        if ( name in this.options.responsive[ bp ]) {
          optValue = this.options.responsive[ bp ][ name ];
        }
      }
    }
    return optValue;
  }

  goNext() {
    let repeat = this.getOption( 'slideBy' ) === 'page' ? this.getOption( 'items' ) : this.getOption( 'slideBy' );
    for ( let ii = 1; ii <= repeat; ii++ ) {
      this._setNextReference();
      this._movePosition()
    }
    this._renderPosition();
  }

  goPrevious() {
    let repeat = this.getOption( 'slideBy' ) === 'page' ? this.getOption( 'items' ) : this.getOption( 'slideBy' );
    for ( let ii = 1; ii <= repeat; ii++ ) {
      this._setPreviousReference();
      this._movePosition()
    }
    this._renderPosition();
  }

  goTo( slideNumber ) {
    if ( slideNumber >= 0 && slideNumber < this.states.slidesNumber ) {
      this.states.referenceSlide = slideNumber
    }
    this._movePosition();
    this._renderPosition();
  }

  getBreakpoint() {
    let newBp = 0;
    for ( let bp in this.options.responsive ) {
      if (( window.matchMedia( `(min-width: ${bp}px` ).matches )) {
        newBp = bp;
      }
    }
    return newBp;
  }

  load() {
    if ( !this.getOption( 'disable' )) {
      this.states.disable = false;
      this.build();
      this.attach();
      this.run();
    }
  }

  reload() {
    if ( !this.getOption( 'disable' )) {
      this.states.disable = false;
      // this.build();
      this.attach();
      this.run();
    }
  }

  getSlidesTotalWidth() {
    let width = 0;
    this.getAllSlides().forEach(( item ) => {
      width += item.clientWidth
    });

    return width;
  }
  _setNextReference() {
    if (( this.states.referenceSlide + 1 ) < (( this.states.slidesNumber ))) {
      this.states.referenceSlide += 1;
    } else {
      this.states.referenceSlide = 0;
    }
  }

  _renderPosition() {
    this.listNode.style.transform = `translate3d(${this.states.listNodeTransformPosition}%,0,0)`;
  }

  _setPreviousReference() {
    // Check if the previous slide number is not equal to -1
    if ( this.states.referenceSlide > 0 ) {
      this.states.referenceSlide -= 1;
    } else {
      this.states.referenceSlide = this.states.slidesNumber - 1;
    }
  }

  _buildList() {
    this.listNode.classList.add( 'carousel__list' );
  }

  _buildOriginalSlides() {
    this.slides.forEach(( slide ) => {
      slide.classList.add( 'carousel__item' );
    })
  }

  _buildCloneSlides() {
    if ( this.getOption( 'clones' )) {
      this._buildPreviousCloneSlides();
      this._buildNextCloneSlides();
    }
  }

  _buildPreviousCloneSlides() {
    let start = this.states.slidesNumber - this.getOption( 'items' );
    this.previousCloneSlides = this.slides.slice( start ).reverse();
    this.previousCloneSlides = this.previousCloneSlides.map(( slide ) => {
      slide = slide.cloneNode( true );
      slide.classList.add( 'carousel__item--clone' );
      slide.classList.add( 'carousel__item--clone-previous' )
      slide.classList.remove( 'js-is--reference' );
      return slide;
    })
  }

  _buildNextCloneSlides() {
    let start = 0,
      end = this.getOption( 'items' );
    this.nextCloneSlides = this.slides.slice( start, end );
    this.nextCloneSlides = this.nextCloneSlides.map(( slide ) => {
      slide = slide.cloneNode( true );
      slide.classList.add( 'carousel__item--clone' );
      slide.classList.add( 'carousel__item--next' );
      return slide;
    })
  }

  _buildCarouselContainer() {
    let carousel = document.createElement( 'div' );
    carousel.classList.add( 'carousel' );
    if ( this.getOption( 'alignment' ) == 'top' ) {
      carousel.classList.add( 'carousel--top' );
    } else if ( this.getOption( 'alignment' ) == 'bottom' ) {
      carousel.classList.add( 'carousel--bottom' );
    } else if ( this.getOption( 'alignment' ) == 'center' ) {
      carousel.classList.add( 'carousel--center' );
    }
    this.carouselNode = carousel;
  }

  _buildControls() {
    if ( this.getOption( 'controls' )) {
      let controls = document.createElement( 'div' ),
        previous = this._buildPreviousControl(),
        next = this._buildNextControl();
      controls.classList.add( 'carousel__controls' );
      if ( this.getOption( 'controlsAbsolute' )) {
        controls.classList.add( 'carousel__controls--absolute' );
      }
      controls.appendChild( previous );
      controls.appendChild( next );
      this.controlsNode = controls;
    }
  }

  _buildPreviousControl() {
    let control = document.createElement( 'button' );
    control.classList.add( 'carousel__control', 'carousel__control--previous' );
    control.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="icon icon--32" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M10.5 12l9 9-3.1 3-11.9-12 11.9-12 3.1 3-9 9z"/>
      </svg>
    `;
    control.addEventListener( 'click', this.goPrevious.bind( this ))
    return control;
  }

  _buildNextControl() {
    let control = document.createElement( 'button' );
    control.classList.add( 'carousel__control', 'carousel__control--next' );
    control.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="icon icon--32" viewBox="0 0 15 24" aria-hidden="true">
        <path d="M0 3l3.1-3 11.9 12-11.9 12-3.1-3 9-9-9-9z"/>
      </svg>
    `;
    control.addEventListener( 'click', this.goNext.bind( this ))
    return control;
  }

  _buildNav() {
    if ( this.getOption( 'nav' )) {
      let repeat;

      if ( this.getOption( 'slideBy' ) === 'page' ) {
        repeat = Math.ceil( this.slides.length / this.getOption( 'items' ));
      } else if ( this.getOption( 'slideBy' ) > 0 ) {
        repeat = Math.ceil( this.slides.length / this.getOption( 'slideBy' ));
      }

      // eslint-disable-next-line one-var
      let nav = document.createElement( 'nav' );
      nav.classList.add( 'carousel__navigation' );

      if ( this.getOption( 'navAbsolute' )) {
        nav.classList.add( 'carousel__navigation--absolute' );
      }
      for ( let ii = 0; ii < repeat; ii++ ) {
        let multiplicator = isNaN( this.getOption( 'slideBy' )) ? this.getOption( 'items' ) : this.getOption( 'slideBy' ),
          targetSlide = ii * multiplicator,
          navItem = document.createElement( 'button' );
        navItem.textContent = ii + 1;
        navItem.classList.add( 'carousel__navigation-item' );
        navItem.classList.add( `carousel__navigation-item--${targetSlide}` );
        navItem.addEventListener( 'click', this.goTo.bind( this, targetSlide ))
        nav.appendChild( navItem );
      }
      this.navNode = nav;
    }
  }

  _buildWrapper() {
    let wrapper = document.createElement( 'div' );
    wrapper.classList.add( 'carousel__wrapper' );
    this.wrapperNode = wrapper;
  }

  _attachCarousel() {
    if ( !this.states.disable ) {
      this.listNode.parentElement.replaceChild( this.carouselNode, this.listNode );
    }
  }

  _attachControls() {
    if ( this.getOption( 'controls' )) {
      if ( this.getOption( 'controlsAbsolute' )) {
        this.wrapperNode.appendChild( this.controlsNode );
      }
      // else {
      this.carouselNode.appendChild( this.controlsNode );
      // }
    }
  }

  _attachList() {
    if ( !this.states.disable ) {
      this.wrapperNode.appendChild( this.listNode );
    }
  }

  _attachWrapper() {
    if ( !this.states.disable ) {
      this.carouselNode.appendChild( this.wrapperNode );
    }
  }

  _attachNav() {
    if ( this.getOption( 'nav' ) && !this.states.disable ) {
      if ( this.getOption( 'navAbsolute' )) {
        this.wrapperNode.appendChild( this.navNode );
      } else {
        this.carouselNode.appendChild( this.navNode );
      }
    }
  }

  _attachClones() {
    if ( this.getOption( 'clones' ) && !this.states.disable ) {
      this._attachPreviousClones();
      this._attachNextClones();
    }
  }

  _attachPreviousClones() {
    this.previousCloneSlides.forEach(( slide ) => {
      this.listNode.insertBefore( slide, this.listNode.firstElementChild );
    })
  }
  _attachNextClones() {
    this.nextCloneSlides.forEach(( slide ) => {
      this.listNode.appendChild( slide );
    })
  }

  _detachClones() {
    if ( this.getOption( 'clones' )) {
      let clones = [ ...this.previousCloneSlides, ...this.nextCloneSlides ];
      // Check if clones exist in the DOM
      if ( clones.length > 0 && clones[ 0 ].parentElement ) {
        clones.forEach(( slide ) => {
          this.listNode.removeChild( slide );
        })
        this.previousCloneSlides = [];
        this.nextCloneSlides = [];
      }

    }
  }

  _detachNav() {
    if ( !this.getOption( 'nav' ) && this.navNode ) {
      // Check if navNode is in the DOM
      if ( this.navNode.parentElement ) {
        this.navNode.parentElement.removeChild( this.navNode );
      }
    }
  }

  _detachControls() {

    if ( !this.getOption( 'controls' ) && this.controlsNode ) {

      // Check if controlsNode is in the DOM
      if ( this.controlsNode.parentElement ) {
        this.controlsNode.parentElement.removeChild( this.controlsNode );
      }
    }
  }

  _detachWrapper() {
    // Check if Wrapper is in the DOM
    if ( this.wrapperNode.parentElement ) {
      this.wrapperNode.parentElement.removeChild( this.wrapperNode );
    }
  }

  _detachListNode() {
    // Check if listNode is in the DOM
    if ( this.listNode.parentElement ) {
      this.listNode.parentElement.removeChild( this.listNode );
      this.listNode.classList.remove( 'carousel__list' );
      this.listNode.style.transform = '';

      this.slides.forEach(( slide ) => {
        slide.classList.remove( 'carousel__item' )
      })
    }
  }

  _detachCarousel() {
    // Check if Wrapper is in the DOM
    if ( this.carouselNode ) {
      if ( this.carouselNode.parentElement ) {
        this.carouselNode.parentElement.replaceChild( this.listNode, this.carouselNode );
      }
    }
  }

  /* Other Methods */
  _setSlidesSize() {
    this.states.slideSize = 100 / this.getOption( 'items' );

    this.getAllSlides().forEach(( slide ) => {
      slide.style.width = this.states.slideSize + '%';
      slide.style.maxWidth = this.states.slideSize + '%';
      slide.style.minWidth = this.states.slideSize + '%';
    })
  }

  _unsetSlidesSize() {
    this.getAllSlides().forEach(( slide ) => {
      slide.style.width = '';
      slide.style.maxWidth = '';
      slide.style.minWidth = '';
    })
  }

  _movePosition() {
    this._setCurrentPosition();
    // this._removeAllReferenceClasses();
    // this._setReferenceSlide();
    this._setNavActiveSlide()
  }

  _setOriginalreferenceSlide() {
    this.states.referenceSlide = 0;
  }

  _setNavActiveSlide() {
    let number;
    Array.from( this.navNode.children ).forEach(( button ) => {
      button.classList.remove( 'js-is-ref' )
    })
    if ( !isNaN( this.getOption( 'slideBy' ))) {
      number = Math.floor( this.states.referenceSlide );
    } else {
      number = Math.floor( this.states.referenceSlide );
    }

    while (( !this.navNode.querySelector( `.carousel__navigation-item--${number}` )) && ( number >= 0 )) {
      number--;
    }
    this.navNode.querySelector( `.carousel__navigation-item--${number}` ).classList.add( 'js-is-ref' )
  }

  _setCurrentPosition() {
    this.states.listNodeTransformPosition = -(( this.states.referenceSlide * ( 100 / this.getOption( 'items' ))) + ( 100 / this.getOption( 'items' ) * this.previousCloneSlides.length ));
  }

  _removeAllReferenceClasses() {
    this.slides.forEach(( slide ) => {
      slide.classList.remove( 'js-is--reference' );
    })
  }

  _setControlsPosition() {
    if ( this.getOption( 'controls' )) {
      if ( this.getOption( 'nav' )) {
        if ( !this.getOption( 'navAbsolute' ) && this.getOption( 'controlsAbsolute' )) {
          this.controlsNode.style.height = this.controlsNode.querySelector( '.carousel__control' ).offsetHeight + 'px';
          this.controlsNode.style.transform = `translateY(-50%) translateY(-${this.navNode.offsetHeight / 2}px)`;
        }
      } else if ( !this.getOption( 'nav' )) {
        if ( this.getOption( 'controlsAbsolute' )) {
          this.controlsNode.style.height = this.controlsNode.querySelector( '.carousel__control' ).offsetHeight + 'px';
          this.controlsNode.style.transform = 'translateY(-50%)';
        }
      }
    }
  }

  _loadSwipe() {
    if ( this.getOption( 'swipe' )) {
      this.wrapperNode.addEventListener( 'touchstart', this._onTouchStart.bind( this ), false );
      this.wrapperNode.addEventListener( 'mousedown', this._onMouseDown.bind( this ), false );
      // this.wrapperNode.addEventListener('touchmove',this._onTouchMove.bind(this),false);
      this.wrapperNode.addEventListener( 'touchend', this._onTouchEnd.bind( this ), false );
      this.wrapperNode.addEventListener( 'mouseup', this._onMouseUp.bind( this ), false );
    }
  }
  _onTouchStart( e ) {
    this.states.swipeX = this._getRealCoordinate( e.touches[ 0 ].clientX, this.wrapperNode.getBoundingClientRect().left );
  }

  _onTouchEnd( e ) {
    let newX = this._getRealCoordinate( e.changedTouches[ 0 ].clientX, this.wrapperNode.getBoundingClientRect().left ),
      diff = Math.abs( this.states.swipeX - newX );
    if ( diff > this.getOption( 'swipeDistance' )) {
      if ( newX > this.states.swipeX ) {
        this.goPrevious();
      } else if ( newX < this.states.swipeX ) {
        this.goNext();
      }
    }
  }
  _onMouseDown( e ) {
    e.preventDefault();
    this.states.swipeX = this._getRealCoordinate( e.clientX, this.wrapperNode.getBoundingClientRect().left );
  }

  _onMouseUp( e ) {
    let newX = this._getRealCoordinate( e.clientX, this.wrapperNode.getBoundingClientRect().left );
    e.preventDefault();
    // eslint-disable-next-line one-var
    let diff = Math.abs( this.states.swipeX - newX );
    if ( diff > this.getOption( 'swipeDistance' )) {
      if ( newX > this.states.swipeX ) {
        this.goPrevious();
      } else if ( newX < this.states.swipeX ) {
        this.goNext();
      }
    }
  }



  _onTouchMove( e ) {
    e.preventDefault();
    // console.log(this._getRealCoordinate(e.touches[0].clientX,this.wrapperNode.getBoundingClientRect().left));
  }

  _getRealCoordinate( clientX, left ) {
    return clientX - left;
  }

  onWindowResize() {
    // Check if breakpoint change
    if ( this.getBreakpoint() != this.states.breakpoint ) {
      this.states.breakpoint = this.getBreakpoint();
      this.detach();
      this.load();
    }
  }
}
